import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import {
  useGetBusinessQuery,
  useGetBusinessStoreQuery,
} from "../../store/api";
import { formatDateTime } from "../../utils/reporting";
import { getStoreProblemInfo } from "../../utils/problemDetails";

const getCurrencyCode = (entity) => {
  if (!entity) return "";

  const candidates = [
    entity.currencyCode,
    entity.currency,
    entity.currency?.code,
    entity.currency?.currencyCode,
    entity.defaultCurrencyCode,
    entity.defaultCurrency?.code,
    entity.defaultCurrency?.currencyCode,
  ];

  const resolvedCurrencyCode = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return resolvedCurrencyCode?.trim() || "";
};

const StoreDetail = () => {
  const { businessId, storeId } = useParams();
  const navigate = useNavigate();
  const { data: business } = useGetBusinessQuery(businessId);
  const {
    data: store,
    isLoading,
    isError,
    error,
  } = useGetBusinessStoreQuery({ businessId, storeId });

  const status =
    store?.status ||
    (store?.isActive === true ? "ACTIVE" : store?.isActive === false ? "INACTIVE" : "INACTIVE");
  const isActive = String(status).toUpperCase() === "ACTIVE";
  const city =
    store?.city ||
    store?.address?.city ||
    store?.addressCity ||
    store?.location?.city ||
    "-";
  const state = store?.state || store?.address?.state || "-";
  const countryCode = store?.countryCode || store?.address?.countryCode || "-";
  const gstNumber = store?.gstNumber || "-";
  const currencyCode = getCurrencyCode(store) || getCurrencyCode(business) || "-";
  const postalCode = store?.postalCode || store?.address?.postalCode || "-";
  const addressLine1 = store?.addressLine1 || store?.address?.addressLine1 || "-";
  const addressLine2 = store?.addressLine2 || store?.address?.addressLine2 || "-";

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Store Details"
          subtitle="Loading store details..."
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Back to Stores
            </Button>
          }
        />
        <Card>
          <p className="text-sm text-slate-500">Loading store details...</p>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Store Details"
          subtitle="Error loading store"
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Back to Stores
            </Button>
          }
        />
        <Card>
          <Alert tone="error">
            {getStoreProblemInfo(error, { operation: "load" }).message}
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Store Details"
        subtitle={`View store information for ${business?.businessName || "this business"}.`}
        actions={
          <div className="flex gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Back to Stores
            </Button>
            <Link to={`/businesses/${businessId}/stores/${storeId}/features`}>
              <Button size="sm" variant="secondary">Add Feature</Button>
            </Link>
            <Link to={`/businesses/${businessId}/stores/${storeId}/edit`}>
              <Button size="sm">Edit Store</Button>
            </Link>
          </div>
        }
      />
      <Card>
        <dl className="grid gap-4 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-slate-600">Store Name</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {store?.storeName || store?.name || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Status</dt>
            <dd className="mt-1">
              <Badge tone={isActive ? "success" : "neutral"}>{status}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Store Code</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {store?.storeCode || store?.code || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Store Slug</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {store?.storeSlug || store?.slug || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">City</dt>
            <dd className="mt-1 text-sm text-slate-900">{city}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">State</dt>
            <dd className="mt-1 text-sm text-slate-900">{state}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Country Code</dt>
            <dd className="mt-1 text-sm text-slate-900">{countryCode}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">GST Number</dt>
            <dd className="mt-1 text-sm text-slate-900">{gstNumber}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Currency Code</dt>
            <dd className="mt-1 text-sm text-slate-900">{currencyCode}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Postal Code</dt>
            <dd className="mt-1 text-sm text-slate-900">{postalCode}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">
              Address Line 1
            </dt>
            <dd className="mt-1 text-sm text-slate-900">{addressLine1}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">
              Address Line 2
            </dt>
            <dd className="mt-1 text-sm text-slate-900">{addressLine2}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">QR Base URL</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {store?.qrBaseUrl || store?.qr?.baseUrl || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Edge Public Base URL</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {store?.edgePublicBaseUrl || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">
              Restaurant ID
            </dt>
            <dd className="mt-1 text-sm font-mono text-slate-700">
              {store?.restaurantId || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Store ID</dt>
            <dd className="mt-1 text-sm font-mono text-slate-700">
              {store?.storeId || store?.id || storeId}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Business ID</dt>
            <dd className="mt-1 text-sm font-mono text-slate-700">
              {store?.businessId || businessId}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Created At</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {formatDateTime(store?.createdAt || store?.createdOn)}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-slate-600">Updated At</dt>
            <dd className="mt-1 text-sm text-slate-900">
              {formatDateTime(store?.updatedAt || store?.updatedOn)}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
};

export default StoreDetail;
